import { Percentiles } from 'features/augmented-writing/augmented-writing.types';
import { RecruiterLeadPayload, RecruiterTalentPayload, RefinedRecruiterSearchPayload } from 'features/co-pilot/co-pilot.types';
import type { OfferFieldValue } from 'features/co-pilot/campaign-builder/offer-field.utils';

/**
 * Offer fields may be either a plain string (legacy / initial DB load) or
 * `{ inUse: boolean, value: string }` once the user toggles or edits the
 * field in the campaign builder. Always normalize via
 * `coerceOfferFieldToString(...)` before sending to APIs that expect strings.
 */
export interface Offer {
  jobTitle: OfferFieldValue;
  jobOffer: OfferFieldValue;
  problems: OfferFieldValue;
  features: OfferFieldValue;
  benefits: OfferFieldValue;
  impact: OfferFieldValue;

  /** Map of custom placeholders ({{key}} => value) */
  customPlaceholders?: Record<string, string>;
}

export interface CustomPlaceholder {
  key: string;
  value: string;
}

export interface TalentSearchFeedbackSuggestion {
  type: 'change_title' | 'broaden_location' | 'general';
  text: string;
  currentValue?: string;
  suggestedValue?: string;
}

export interface TalentSearchFeedback {
  message: string;
  suggestions: TalentSearchFeedbackSuggestion[];
}

export interface LinkedinSearch {
  loading: boolean;
  statusMessage?: string;
  result: LinkedinSearchResult | null;
  searchResults: LinkedinSearchResult[] | null;
  openIndex: number | null;
  addedIndices: number[];
  isEmpty: boolean;
  feedback?: TalentSearchFeedback;
  /** Set when the search could not start, e.g. the briefing lacks the text the AI reads. */
  blockedMessage?: string;
}

export interface SearchContact {
  url?: string;
  name?: string;
  company?: string | null;
  jobTitle?: string | null;
}

export interface LinkedinSearchResult {
  URL?: string;
  payload?: RecruiterTalentPayload | RecruiterLeadPayload | RefinedRecruiterSearchPayload;
  searchName?: string;
  linkedinFreq?: number;
  index?: number | null;
  contacts?: SearchContact[];
  languages?: string[];
  searchRequestId?: string;
  searchHistoryId?: string;
  searchContextId?: string;
}

export interface ContactDetails {
  fullName: string;
  occupation: string;
  companyName: string;
  email: string;
  linkedinUrl: string;
  location: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface ContactAbout {
  experience: {
    title: string;
    companyName: string;
    location: string;
    startDate?: { month: number; year: number } | string;
    endDate?: { month: number; year: number } | string;
  }[];
  education: {
    schoolName?: string;
    degreeName?: string;
    fieldOfStudy?: string;
    startDate?: { year: number } | string;
    endDate?: { year: number } | string;
  }[];
  summary?: string;
  languages?: string[];
  skills: {
    technical: string[];
    industry: string[];
    softSkills: string[];
  };
}

export interface CompanyAbout {
  companyDetails: {
    name: string;
    industry: string;
    employeeCount: string;
    headquarters: string;
    website: string;
    locations: string[];
    founded: string;
    specialties: string;
  };
  description: string;
}

export enum ContactSearchMethods {
  WE_SEARCH = 'WE_SEARCH',
  OWN_SEARCH = 'OWN_SEARCH',
  UPLOAD_CONTACT_LIST = 'UPLOAD_CONTACT_LIST',
  ADD_FROM_CAMPAIGN = 'ADD_FROM_CAMPAIGN',
}

export enum OutreachTypes {
  COLD = 'cold',
  IN_MARKET = 'in-market',
}

export enum CampaignBuilderSteps {
  CAMPAIGN_BRIEFING = 'campaignBriefing',
  CAMPAIGN_CONTACT_LIST = 'campaignContactList',
  CAMPAIGN_FLOW = 'campaignFlow',
  CAMPAIGN_TESTING = 'campaignTesting',
  CAMPAIGN_SETTINGS = 'campaignSettings',
  CAMPAIGN_SUMMARY = 'campaignSummary',
}

export enum CampaignPipelineSteps {
  ADD_CONTACTS = 'add-contacts',
  AI_CONTACT_QUALIFICATION = 'ai-contact-qualification',
  MANAGE_SEARCHES = 'manage-searches',
  MANAGE_BLACKLISTS = 'manage-blacklists',
  CONTACT_OVERVIEW = 'contact-overview',
  FINAL_CONTACT_LIST = 'final-contact-list',
}

export enum SelectorIds {
  STEP_CONTENT = 'step-content',
  SEARCH_METHOD = 'select-search-method',
  OWN_SEARCH_OBJECTIVE = 'select-own-search-objective',
  LINKEDIN_URLS = 'paste-linkedin-urls',
  CRAWLING_ACCOUNT = 'select-crawling-account',
  CONTACT_LIST = 'upload-contact-list',
  LINKEDIN_PRODUCT = 'select-linkedin-product',
  PAI_SEARCH_OBJECTIVE = 'select-pai-search-objective',
  SEARCH_LOCATION = 'type-search-location',
  PAI_SEARCH_LANGUAGE = 'pai-search-language',
  PAI_SEARCH_NETWORK = 'pai-search-network',
  PAI_SEARCH_START_SEARCH = 'pai-search-start-search',
  PAI_SEARCH_SAVE_SEARCH = 'pai-search-save-search',
  SETTINGS_APPROVE_MESSAGES = 'settings-linkedin-account',
  SETTINGS_LINKEDIN_ACCOUNT = 'settings-approve-messages',
  SETTINGS_RECONTACT_COOLDOWN = 'settings-recontact-cooldown',
  SETTINGS_TIMEZONE = 'settings-timezone',
  SETTINGS_WORK_TIME = 'settings-work-time',
  SETTINGS_DAILY_MESSAGE_LIMITS = 'settings-daily-message-limits',
  FILE_UPLOAD_ADD_CONTACTS = 'file-upload-add-contacts',
}

export interface QualificationPromptPlaceholders {
  required: string;
  disqualifying: string;
  preferred: string;
  // old keys, to be removed
  additionalContactInfo?: string;
  companyCharacteristics?: string;
  companyChallenges?: string;
  organizationalWorkingCultureValues?: string;
}

export interface SearchObjective {
  key: string;
  outreachType: OutreachTypes;
  title: string;
  subTitle: string;
  whenToUseText: string;
  howToUseText: string;
  exampleUrl: string;
  regex: RegExp;
}

export interface SingleSearch {
  name: string;
  url: string;
  numberOfContactsToLoad: number;
  targetJobTitles?: string[];
}

export interface CrawlerOptions {
  includeReactions?: boolean;
  includeComments?: boolean;
  includeAuthor?: boolean;
  includedProfileDegrees?: number[];
}

export interface SearchToAdd {
  key: string;
  title: string;
  subTitle: string;
  regex: RegExp;
  search: SingleSearch;
  crawlerOptions?: CrawlerOptions;
}

export interface ContactToAdd {
  name?: string;
  url?: string;
  email?: string;
  currentCompanies?: string[];
  customField?: string;
}

export interface CampaignTemplate {
  _id: string;
  /** Legacy key (e.g. activeSourcingDuLessInmails) for seeded entries, otherwise the id. */
  key: string;
  name: string;
  description?: string;
  /** Free-text "for whom" line. */
  audience?: string;
  language?: 'de' | 'en' | 'auto';
  formalSpeech?: boolean;
  contactAudience?: 'new' | 'existing' | 'both';
  visibility?: 'public' | 'tenant';
  stepCount?: number;
}

/** Kontaktanfragen: Länge und was die Versandkette damit getan hat (wie beim echten Versand). */
export interface ConnectionNoteCheck {
  characters: number;
  limit: number;
  originalCharacters: number;
  outcome: 'within_limit' | 'shortened' | 'regenerated' | 'too_long';
}

export interface TestMessagePreview {
  stepIndex: number;
  functionName: string;
  text: string;
  subject?: string;
  percentiles: Percentiles;
  /** Nummer der Nachricht im Ablauf — dieselbe wie im MCP (campaign_messages_list). */
  number?: number | null;
  /** Die KI konnte keine Nachricht schreiben; `text` ist ein Hinweis zur Anweisung. */
  guidanceUsed?: boolean;
  suggestedPrompt?: string;
  connectionNote?: ConnectionNoteCheck;
}

export interface TestContactPreview {
  contact: {
    name: string;
    assessment: string | undefined;
  };
  messages: TestMessagePreview[];
  /** Schritte ohne Nachricht, mit Grund. */
  skipped?: { stepIndex: number; functionName: string; reason: string; number?: number | null }[];
}

/** Herkunft des angezeigten Tests — App und MCP teilen sich den letzten Test (2 h). */
export interface CampaignTestMeta {
  testId: string;
  source: 'app' | 'mcp';
  status: 'running' | 'done' | 'failed' | 'interrupted';
  startedAt: string;
  finishedAt: string | null;
}

export interface CampaignTestingState {
  selectedTestContacts: string[];
  loading: boolean;
  testContacts: TestContactPreview[];
  activeTestContactIndex: number;
  activeMessageIndex: number;
  lastTest?: CampaignTestMeta | null;
}

export enum FlowCreationTabs {
  FROM_TEMPLATE = 'from_template',
  FROM_SCRATCH = 'from_scratch',
  FROM_EXISTING_CAMPAIGN = 'from_existing_campaign',
}

export enum Qualifications {
  NOT_JUDGED = 'not-judged',
  A = 'A',
  B = 'B',
  C = 'C',
  ERROR = 'Error',
}

export enum ContactFilter {
  ALL = 'all',
  IS_ACTIVE = 'isActive',
  IS_NOT_ACTIVE = 'isNotActive',
  IS_FINISHED = 'isFinished',
  CONTACTED = 'contacted',
  NOT_CONTACTED = 'notContacted',
  BLACKLISTED = 'blacklisted',
  SUSPENDED = 'suspended',
  DELETED = 'deleted',
  ALREADY_CONTACTED = 'duplicates',
  /**
   * Held back by the double-contact guard. Distinct from ALREADY_CONTACTED, which means
   * "we have written to them in *this* campaign"; this one means "another campaign got
   * there first, so this one deliberately left them alone". The API has served
   * `?filter=contactedElsewhere` all along — nothing offered it (7b1c596f / 40db0ad9).
   */
  CONTACTED_ELSEWHERE = 'contactedElsewhere',
  UNCERTAIN_SEX = 'uncertainSex',
  PROCESSED = 'processed',
  NOT_PROCESSED = 'notProcessed',
}

export enum BlacklistScopes {
  GLOBAL = 'global',
  LOCAL = 'local',
}

export enum BlacklistUploadVariant {
  FILE = 'file',
  INDIVIDUAL = 'individual',
  LINKEDIN_COMPANY_PICKER = 'linkedin-company-picker',
}

export interface AutoReplyCase {
  promptToIdentify: string;
  promptToReply: string;
}

export interface AutoReply {
  isActive: boolean;
  goalofCommunication?: string;
  caseHandling: AutoReplyCase[];
}

export interface ContactFunnelResults {
  searches: {
    current: number;
    total: number;
  };
  judged: {
    current: number;
    total: number;
  };
  active: {
    current: number;
    total: number;
  };
}

export enum StartNewOrClone {
  START_NEW = 'start_new',
  CLONE_EXISTING = 'clone_existing',
}

export enum EnrichmentVariants {
  SHORT_LIST = 'short-list',
  ALL = 'all',
}
export enum DrawerTabs {
  ASSESSMENT_OVERVIEW = 'assessmentOverview',
  ABOUT_CONTACT = 'aboutContact',
  ABOUT_COMPANY = 'aboutCompany',
}

export interface SankeyLink {
  source: string;
  target: string;
  value: number;
}

export enum SankeyNodeNames {
  MANUALLY_ADDED = 'manuallyAdded',
  CRAWLABLE = 'crawlable',
  CRAWLED = 'crawled',
  PROFILE_VISITED = 'profileVisited',
  PROFILE_NOT_VISITED = 'profileNotVisited',
  USEABLE_CONTACTS = 'useableContacts',
  A = 'a',
  B = 'b',
  C = 'c',
  NOT_JUDGED_YET = 'notJudgedYet',
  AI_JUDGING_ERROR = 'aiJudgingError',
  JUDGED_IN = 'judgedIn',
  JUDGED_OUT = 'judgedOut',
  UNCERTAIN_SEX = 'uncertainSex',
  CONTACTED_ELSEWHERE = 'contactedElsewhere',
  DELETED = 'deleted',
  SUSPENDED = 'suspended',
  BLACKLISTED = 'blacklisted',
  OUT_OF_NETWORK = 'outOfNetwork',
  CRAWLED_TWICE = 'crawledTwice',
  NOT_CRAWLED = 'notCrawled',
  CRAWLING_BUFFER = 'crawlingBuffer',
  OTHER_NOT_CRAWLED = 'otherNotCrawled',
}
